import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Flex from '@react-css/flex';
import type { Elevation } from 'storybookConfig/mixins/elevate';
import elevate from 'storybookConfig/mixins/elevate';
import lift from 'storybookConfig/mixins/lift';
import { ThemeBreakpoints } from 'storybookConfig/stories/theme';
import BodyMolecule, { type BodyProps } from 'storybookConfig/stories/molecules/Body';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Adds a drop shadow on top of the card's default border. Off by default.
   */
  elevation?: Elevation | 'none';
  /**
   * Padding
   */
  padding?: string;
  /**
   * isFullWidth
   */
  $isFullWidth?: boolean;
  /**
   * isFullWidth
   */
  gap?: string;
  /**
   * A clickable card: an iris edge and a lift on hover and keyboard focus.
   */
  $interactive?: boolean;
  /**
   * Held lifted, for a selected card.
   */
  $selected?: boolean;

  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

const Base = styled((props: CardProps) => <Flex {...props} column />, {
  shouldForwardProp: (prop) =>
    !['elevation', 'padding', '$isFullWidth', '$interactive', '$selected'].includes(prop),
})`
  background-color: ${({ theme }) => theme.color.surfaceCanvas};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: 8px;
  /* Without a padding of its own, 16px on a phone and 24px 32px from md. */
  ${({ padding }) =>
    padding
      ? `padding: ${padding};`
      : css`
          padding: 16px;

          @media screen and (${ThemeBreakpoints.md}) {
            padding: 24px 32px;
          }
        `}

  ${({ gap = '24px' }) => `gap: ${gap}`};

  ${({ elevation }) => elevation && elevation !== 'none' && elevate(elevation)};

  ${({ $isFullWidth }) => $isFullWidth && 'width: 100%'};

  ${({ $interactive, $selected }) =>
    ($interactive || $selected) && lift({ interactive: !!$interactive, selected: !!$selected })};
`;

const Title = styled((props: BodyProps) => <BodyMolecule {...props} variant="Headings/H3" />)<{
  children: React.ReactNode;
}>``;

const Head = styled(Flex.Item)`
  padding-bottom: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.color.border};

  ${Title} + button {
    max-height: 24px;
  }
`;

const Body = styled(Flex.Item)``;

/**
 * A simple, visually distinct Card UI. It supports optional `Card.Head` and `Card.Body` subcomponents,
 * which can provide layout for content. If no `Card.Head` is necessary you may omit `Card.Body`.
 */
const Card = Object.assign(Base, { Head, Title, Body });

export default Card;
